import { ViewBooking } from "collins-js-sdk";
import {
  CheckCircle,
  ChevronDown,
  Clock,
  Edit,
  Eye,
  LogOut,
  MessageCircle,
  XCircle
} from "lucide-react";
import React from "react";
import { VenueGroupConfig } from "../../../../../../../types/VenueGroup";
import { getDevice } from "../../../../../../../utils/device";
import { BookingStage, VenueConfig } from "../../../../../../../types/venues";
import {
  getExcludedStages,
  getBookingStages as getVenueBookingStages
} from "../../../../../../../utils/booking";
import { waitlistBookingsFilter } from "../../../../../../../utils/bookingFilters";
import { Button } from "../../../../../../ui/button";
import { WebViewPopover } from "../../../../SubHeader/WebViewPopover";
import { getStageColor, renderStageIcon } from "../utils/bookingCardUtils";

/* NOTE: The stage selection dropdown for waitlisted bookings has been moved
   to the DiaryBookingCardStatusIndicator (left stage indicator), matching
   the collins-mfe DiaryBookingCard pattern. Waitlisted cards now only show
   Check In, SMS, and No Show inline actions here. */

interface DiaryBookingCardActionButtonsProps {
  booking: ViewBooking;
  isOnRunSheet?: boolean;
  isUpdating: boolean;
  optimisticStage: string | null | undefined;
  currentStage: BookingStage | null;
  venueConfig: VenueConfig;
  venueGroupConfig: VenueGroupConfig;
  isPopoverOpen: boolean;
  setIsPopoverOpen: (open: boolean) => void;
  setShowSendSms: (show: boolean) => void;
  setShowPreviewModal: (show: boolean) => void;
  setShowEditBooking: (show: boolean) => void;
  swipeoutOptionClick: (name: string, booking: ViewBooking) => void;
  getFirstCheckedInStage: () => string;
  bookingSmsEnabled?: boolean;
}

export const DiaryBookingCardActionButtons =
  React.memo<DiaryBookingCardActionButtonsProps>(
    ({
      booking,
      isOnRunSheet,
      isUpdating,
      optimisticStage,
      currentStage,
      venueConfig,
      venueGroupConfig,
      isPopoverOpen,
      setIsPopoverOpen,
      setShowSendSms,
      setShowPreviewModal,
      setShowEditBooking,
      swipeoutOptionClick,
      getFirstCheckedInStage,
      bookingSmsEnabled
    }) => {
      const isDesktop = getDevice() === "desktop";
      const isActivelyWaitlisted = waitlistBookingsFilter(booking);

      if (!isOnRunSheet && !isActivelyWaitlisted) {
        return null;
      }

      const effectiveStageForButton =
        optimisticStage !== null ? optimisticStage : booking.currentStage;
      const isUnchecked =
        !effectiveStageForButton || effectiveStageForButton === "";
      const shouldShowCheckIn =
        isUnchecked && (!isUpdating || optimisticStage === "");

      const renderStageSelectionDropdown = () => {
        if (
          !booking.currentStage ||
          booking.currentStage === "left" ||
          booking.currentStage === "no-show" ||
          booking.currentStage === "rejected"
        ) {
          return null;
        }

        const stages: BookingStage[] =
          venueConfig && venueGroupConfig
            ? getVenueBookingStages({ venueConfig, venueGroupConfig })
            : [];

        const excludedStages = getExcludedStages();

        const availableStages = stages.filter(
          (stage: BookingStage) =>
            !excludedStages.includes(stage.id.toLowerCase()) &&
            stage.id !== booking.currentStage
        );

        return (
          <WebViewPopover
            side="bottom"
            sideOffset={8}
            open={isPopoverOpen}
            onOpenChange={setIsPopoverOpen}
            content={
              <div className="p-2 space-y-1">
                <button
                  className="w-full px-3 py-2 text-left text-sm rounded-md flex items-center gap-3 hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    swipeoutOptionClick("uncheck", booking);
                  }}
                >
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-black text-xs font-bold relative"
                    style={{
                      backgroundColor: getStageColor(
                        "",
                        venueConfig,
                        venueGroupConfig
                      )
                    }}
                  >
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{
                        backgroundColor: "rgba(255, 255, 255, 0.5)"
                      }}
                    />
                    <Clock className="w-3 h-3 relative z-10" />
                  </div>
                  <span>Uncheck</span>
                </button>

                {availableStages.map((stage: BookingStage) => (
                  <button
                    key={stage.id}
                    className="w-full px-3 py-2 text-left text-sm rounded-md flex items-center gap-3 hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                    onClick={(e) => {
                      e.stopPropagation();
                      swipeoutOptionClick(stage.id, booking);
                    }}
                  >
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-black text-xs font-bold relative"
                      style={{
                        backgroundColor: getStageColor(
                          stage.id,
                          venueConfig,
                          venueGroupConfig
                        )
                      }}
                    >
                      <div
                        className="absolute inset-0 rounded-full"
                        style={{
                          backgroundColor: "rgba(255, 255, 255, 0.5)"
                        }}
                      />
                      <span className="relative z-10">
                        {stage.id === "" ? (
                          <Clock className="w-3 h-3" />
                        ) : (
                          stage.label.charAt(0).toUpperCase()
                        )}
                      </span>
                    </div>
                    <span>{stage.label}</span>
                  </button>
                ))}
              </div>
            }
          >
            <button
              className={`action-btn action-btn-dropdown flex items-center justify-between ${
                isUpdating ? "opacity-50 cursor-not-allowed" : ""
              }`}
              style={{ width: "auto", minWidth: "100px" }}
              onClick={(e) => {
                e.stopPropagation();
                if (!isUpdating) {
                  setIsPopoverOpen(!isPopoverOpen);
                }
              }}
              disabled={isUpdating}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center text-black text-xs font-bold relative"
                  style={{
                    backgroundColor: getStageColor(
                      booking.currentStage ?? "",
                      venueConfig,
                      venueGroupConfig
                    )
                  }}
                >
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.5)"
                    }}
                  />
                  {renderStageIcon(
                    isUpdating || optimisticStage !== null
                      ? optimisticStage
                      : booking.currentStage,
                    venueConfig,
                    venueGroupConfig
                  )}
                </div>
                <span>
                  {currentStage?.label ??
                    (optimisticStage && optimisticStage !== ""
                      ? (getVenueBookingStages({
                          venueConfig,
                          venueGroupConfig
                        }).find((stage) => stage.id === optimisticStage)
                          ?.label ?? "Loading...")
                      : "Unchecked")}
                </span>
              </div>
              <ChevronDown className="w-4 h-4 ml-1" />
            </button>
          </WebViewPopover>
        );
      };

      if (!isOnRunSheet && isDesktop && isActivelyWaitlisted) {
        return (
          <div className="flex gap-1.5 pt-1 flex-wrap items-center">
            {booking.phone && bookingSmsEnabled && (
              <Button
                size="sm"
                variant="outline"
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded min-h-[28px] touch-manipulation"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setShowSendSms(true);
                }}
              >
                <MessageCircle className="w-3.5 h-3.5" />
                Text
              </Button>
            )}

            <Button
              size="sm"
              variant="outline"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded min-h-[28px] touch-manipulation ml-auto"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                swipeoutOptionClick("no-show", booking);
              }}
            >
              <XCircle className="w-3.5 h-3.5" />
              No Show
            </Button>
          </div>
        );
      }

      // Mobile waitlisted: show only the Text button
      if (!isOnRunSheet && !isDesktop && isActivelyWaitlisted) {
        if (!booking.phone || !bookingSmsEnabled) {
          return null;
        }
        return (
          <div className="flex gap-1.5 pt-1 flex-wrap items-center">
            <Button
              size="sm"
              variant="outline"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded min-h-[28px] touch-manipulation"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setShowSendSms(true);
              }}
            >
              <MessageCircle className="w-3.5 h-3.5" />
              Text
            </Button>
          </div>
        );
      }

      return (
        <div className="booking-actions">
          <div className="booking-actions-left">
            {isUpdating ? (
              <div className="action-btn action-btn-dropdown flex items-center justify-between px-3 py-2 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                  Updating...
                </div>
                <div className="w-4 h-4"></div>
              </div>
            ) : (
              <>
                {shouldShowCheckIn && (
                  <Button
                    size="sm"
                    variant="default"
                    className="action-btn action-btn-primary"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      swipeoutOptionClick(getFirstCheckedInStage(), booking);
                    }}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Check In
                  </Button>
                )}

                {renderStageSelectionDropdown()}
              </>
            )}

            {booking.phone && bookingSmsEnabled && (
              <Button
                size="sm"
                variant="outline"
                className="action-btn"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setShowSendSms(true);
                }}
              >
                <MessageCircle className="w-4 h-4 mr-1" />
                Text
              </Button>
            )}

            <Button
              size="sm"
              variant="outline"
              className="action-btn"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setShowPreviewModal(true);
              }}
            >
              <Eye className="w-4 h-4 mr-1" />
              View
            </Button>

            <Button
              size="sm"
              variant="outline"
              className="action-btn"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setShowEditBooking(true);
              }}
            >
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </Button>
          </div>

          <div className="booking-actions-right">
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="action-btn"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  swipeoutOptionClick("no-show", booking);
                }}
              >
                <XCircle className="w-4 h-4 mr-1" />
                No Show
              </Button>

              {booking.currentStage &&
              booking.currentStage !== "left" &&
              booking.currentStage !== "no-show" &&
              booking.currentStage !== "rejected" &&
              booking.currentStage &&
              booking.currentStage !== "" ? (
                <Button
                  size="sm"
                  variant="secondary"
                  className="action-btn"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    swipeoutOptionClick("left", booking);
                  }}
                >
                  <LogOut className="w-4 h-4 mr-1" />
                  Left
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      );
    }
  );

DiaryBookingCardActionButtons.displayName = "DiaryBookingCardActionButtons";
